import type { Project } from '../types';

const COPY_PREFIX = 'netprobuilder-working-copy:';

export interface ProjectWorkingCopy {
  savedAt: number;
  project: Project;
  customizations: Record<string, { config: any }>;
}

const copyKey = (projectId: string) => `${COPY_PREFIX}${projectId}`;

function reviveProject(raw: any): Project {
  return {
    ...raw,
    createdAt: new Date(raw.createdAt),
    updatedAt: new Date(raw.updatedAt),
    designs: Array.isArray(raw.designs)
      ? raw.designs.map((d: any) => ({
          ...d,
          createdAt: new Date(d.createdAt),
          updatedAt: new Date(d.updatedAt),
        }))
      : raw.designs,
  };
}

export function writeProjectWorkingCopy(
  project: Project,
  customizations: Record<string, { config: any }>
): void {
  try {
    const payload: ProjectWorkingCopy = {
      savedAt: Date.now(),
      project,
      customizations,
    };
    localStorage.setItem(copyKey(project.id), JSON.stringify(payload));
  } catch (error) {
    console.warn('Failed to write project working copy:', error);
  }
}

export function readProjectWorkingCopy(projectId: string): ProjectWorkingCopy | null {
  try {
    const raw = localStorage.getItem(copyKey(projectId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.project) return null;
    return {
      savedAt: typeof parsed.savedAt === 'number' ? parsed.savedAt : 0,
      project: reviveProject(parsed.project),
      customizations: parsed.customizations && typeof parsed.customizations === 'object'
        ? parsed.customizations
        : {},
    };
  } catch (error) {
    console.warn('Failed to read project working copy:', error);
    return null;
  }
}

export function clearProjectWorkingCopy(projectId: string): void {
  try {
    localStorage.removeItem(copyKey(projectId));
  } catch {
    // ignore
  }
}

export function workingCopyIsNewer(copy: ProjectWorkingCopy, incoming: Project): boolean {
  const copyTime = Math.max(
    copy.savedAt,
    new Date(copy.project.updatedAt).getTime(),
    ...(copy.project.designs || []).map((d) => new Date(d.updatedAt).getTime())
  );
  const incomingTime = Math.max(
    new Date(incoming.updatedAt).getTime(),
    ...(incoming.designs || []).map((d) => new Date(d.updatedAt).getTime())
  );
  return Number.isFinite(copyTime) && copyTime > incomingTime;
}
